import config from '@/config'
import {
  CmsFieldConfig,
  CmsFormState,
  CmsFormValue,
  CmsLookupContext,
  CmsResourceConfig,
  CmsTableColumn,
  ICmsAsset,
  CmsEntity,
} from './cms.types'

export const getAbsoluteCmsUrl = (url?: string) => {
  if (!url) return ''

  const apiUrl = config.API_URL.replace(/\/$/, '')
  const cmsPathIndex = url.indexOf('/v1/cms/')

  if (cmsPathIndex !== -1) {
    return `${apiUrl}${url.slice(cmsPathIndex)}`
  }

  if (url.startsWith('http')) return url
  return `${apiUrl}${url.startsWith('/') ? url : `/${url}`}`
}

export const getAbsoluteAssetUrl = getAbsoluteCmsUrl

const isImageUrl = (value?: string) => Boolean(value && /\.(png|jpg|jpeg|webp|gif|svg)$/i.test(value))

const getAssetByUrl = (assets: ICmsAsset[], url?: string) => {
  return assets.find((asset) => asset.url === url)
}

const getInitialFieldValue = <T extends Record<string, any>>(
  field: CmsFieldConfig<T>,
  context: CmsLookupContext
): CmsFormValue => {
  if (field.defaultValue !== undefined) return field.defaultValue
  if (field.kind === 'number') return 0
  if (field.kind === 'boolean') return false
  if (field.kind === 'select') {
    const options = field.getOptions ? field.getOptions(context) : field.options || []
    const firstOption = options.find((option) => 'value' in option)
    return firstOption && 'value' in firstOption ? firstOption.value : ''
  }

  return ''
}

export const createEmptyFormState = <T extends CmsEntity & Record<string, any>>(
  resource: CmsResourceConfig<T>,
  context: CmsLookupContext
): CmsFormState => {
  return resource.fields.reduce<CmsFormState>((state, field) => {
    state[field.name] = getInitialFieldValue(field, context)
    return state
  }, {})
}

export const createFormStateFromItem = <T extends CmsEntity & Record<string, any>>(
  resource: CmsResourceConfig<T>,
  item: T
): CmsFormState => {
  return resource.fields.reduce<CmsFormState>((state, field) => {
    const value = item[field.name]
    state[field.name] = value ?? ''
    return state
  }, {})
}

export const buildPayload = <T extends CmsEntity & Record<string, any>>(
  resource: CmsResourceConfig<T>,
  form: CmsFormState
) => {
  return resource.fields.reduce<Record<string, CmsFormValue | null>>((payload, field) => {
    const value = form[field.name]

    if (field.kind === 'number') {
      payload[field.name] = Number(value || 0)
      return payload
    }

    if (field.kind === 'boolean') {
      payload[field.name] = Boolean(value)
      return payload
    }

    if (field.nullable && value === '') {
      payload[field.name] = null
      return payload
    }

    payload[field.name] = value
    return payload
  }, {})
}

export const validateForm = <T extends CmsEntity & Record<string, any>>(
  resource: CmsResourceConfig<T>,
  form: CmsFormState
) => {
  return resource.fields.reduce<Record<string, string>>((errors, field) => {
    const value = form[field.name]
    const empty = value === '' || value === null || value === undefined

    if (field.required && empty) {
      errors[field.name] = 'Заполните поле'
    }

    if (field.kind === 'number' && Number(value) < 0) {
      errors[field.name] = 'Значение не может быть меньше 0'
    }

    return errors
  }, {})
}

export const filterItems = <T extends CmsEntity & Record<string, any>>(
  items: T[],
  resource: CmsResourceConfig<T>,
  search: string,
  context: CmsLookupContext
) => {
  const normalized = search.trim().toLowerCase()
  if (!normalized) return items

  return items.filter((item) => {
    const values = resource.searchFields.map((field) => String(item[field] ?? '').toLowerCase())
    const columnValues = resource.columns.map((column) => String(column.render(item, context) ?? '').toLowerCase())
    return [...values, ...columnValues].some((value) => value.includes(normalized))
  })
}

export const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} Б`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} КБ`
  return `${(size / 1024 / 1024).toFixed(1)} МБ`
}

export const cmsUtils = {
  getAbsoluteCmsUrl,
  getAbsoluteAssetUrl,
  isImageUrl,
  getAssetByUrl,
  createEmptyFormState,
  createFormStateFromItem,
  buildPayload,
  validateForm,
  filterItems,
  formatFileSize,
}

export type CmsColumnBuilder<T extends CmsEntity & Record<string, any>> = CmsTableColumn<T>
